import { useEffect, useMemo, useState } from "react";
import { Mail, Phone, MapPin, Briefcase, Award, Network, Headphones, Megaphone, Share2, Check, X, PlayCircle, ShoppingBag, Wrench } from "lucide-react";
import { fetchDealers, fetchLocations } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { InquiryForm } from "@/components/InquiryForm";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BENEFITS = [
  { icon: MapPin, title: "Exclusive Territory Opportunities" },
  { icon: Briefcase, title: "Direct Billing Support" },
  { icon: Award, title: "Technical Training" },
  { icon: Headphones, title: "Project Assistance" },
  { icon: Megaphone, title: "Marketing Support" },
  { icon: Share2, title: "Lead Sharing" },
  { icon: Network, title: "Business Development Guidance" },
  { icon: Award, title: "Premium European Brand Portfolio" },
];

export default function Dealers() {
  const [dealers, setDealers] = useState([]);
  const [locations, setLocations] = useState({ states: [], locations: [] });
  const [state, setState] = useState("all");
  const [city, setCity] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLocations().then(setLocations).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchDealers({
      ...(state !== "all" && { state }),
      ...(city !== "all" && { city }),
    })
      .then(setDealers)
      .catch(() => setDealers([]))
      .finally(() => setLoading(false));
  }, [state, city]);

  const availableCities = useMemo(() => {
    if (state === "all") return [];
    const found = locations.locations.find((l) => l.state === state);
    return found?.cities || [];
  }, [state, locations]);

  return (
    <>
      <section className="pt-40 pb-20" data-testid="dealers-hero">
        <div className="container-edge max-w-4xl">
          <p className="eyebrow flex items-center gap-3"><span className="divider-gold" /> Dealer Network</p>
          <h1 className="display-1 mt-6">
            Join the <span className="italic text-gold">Fibaro</span> partner network.
          </h1>
          <p className="mt-8 text-base md:text-lg text-muted-foreground leading-relaxed">
            We are actively expanding our nationwide network of premium Fibaro partners. Bring your
            expertise — we'll bring world-class technology, training and ongoing support.
          </p>
        </div>
      </section>

      {/* Why partner */}
      <section className="py-20 md:py-24 bg-secondary/40" data-testid="dealer-benefits">
        <div className="container-edge">
          <Reveal>
            <SectionHeader eyebrow="Why Partner With Us?" title={<>Eight reasons to <span className="italic text-gold">join us</span>.</>} />
          </Reveal>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={(i % 4) * 80} className="bg-background p-8 group">
                <b.icon className="h-6 w-6 text-gold transition-transform group-hover:-translate-y-1" />
                <p className="mt-6 text-sm font-medium leading-snug">{b.title}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Locator */}
      <section className="py-24 md:py-32" data-testid="dealer-locator">
        <div className="container-edge">
          <Reveal>
            <SectionHeader eyebrow="Dealer Locator" title={<>Find a <span className="italic text-gold">Fibaro</span> partner near you.</>} />
          </Reveal>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-2">State</label>
              <Select value={state} onValueChange={(v) => { setState(v); setCity("all"); }}>
                <SelectTrigger data-testid="locator-state" className="h-12 rounded-none border-x-0 border-t-0 border-b-2 bg-transparent">
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {locations.states.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.24em] text-muted-foreground mb-2">City</label>
              <Select value={city} onValueChange={setCity} disabled={state === "all"}>
                <SelectTrigger data-testid="locator-city" className="h-12 rounded-none border-x-0 border-t-0 border-b-2 bg-transparent">
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {availableCities.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 lg:col-span-2 text-xs uppercase tracking-[0.22em] text-muted-foreground">
              <span data-testid="locator-count">{loading ? "Loading…" : `${dealers.length} partner${dealers.length === 1 ? "" : "s"} found`}</span>
            </div>
          </div>

          <div className="mt-10 flex flex-col gap-px bg-border border border-border">
            {dealers.map((d, i) => (
              <Reveal key={d.id || i} delay={(i % 6) * 60} className="bg-background p-8 md:p-10 group hover:bg-secondary/40 transition-colors">
                <article className="grid md:grid-cols-12 gap-6 md:gap-10 items-start" data-testid={`dealer-card-${i}`}>
                  {/* Left: Identity */}
                  <div className="md:col-span-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="h-px w-6 bg-gold" />
                      <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal-500 dark:text-white/50">
                        {d.type || d.specialization || "Dealer"}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl font-light leading-tight tracking-[-0.03em]" data-testid={`dealer-name-${i}`}>
                      {d.company}
                    </h3>
                    <p className="mt-3 text-xs uppercase tracking-[0.22em] text-muted-foreground">
                      {d.city} · {d.state}
                    </p>
                  </div>

                  {/* Middle: Address + contact */}
                  <div className="md:col-span-4 space-y-3 text-sm">
                    <p className="flex items-start gap-3 text-muted-foreground leading-relaxed">
                      <MapPin className="h-4 w-4 text-gold mt-0.5 shrink-0" />
                      <span>{d.address || `${d.city}, ${d.state}`}</span>
                    </p>
                    {d.phone && (
                      <a href={`tel:${d.phone.replace(/\s/g, "")}`} className="flex items-center gap-3 hover:text-gold transition-colors" data-testid={`dealer-phone-${i}`}>
                        <Phone className="h-4 w-4 text-gold shrink-0" /> {d.phone}
                      </a>
                    )}
                    {d.email && (
                      <a href={`mailto:${d.email}`} className="flex items-center gap-3 hover:text-gold transition-colors break-all" data-testid={`dealer-email-${i}`}>
                        <Mail className="h-4 w-4 text-gold shrink-0" /> {d.email}
                      </a>
                    )}
                  </div>

                  {/* Right: Service capabilities */}
                  <div className="md:col-span-3 md:border-l md:border-border md:pl-8">
                    <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-charcoal-500 dark:text-white/50 mb-4">Services</p>
                    <ul className="space-y-2.5">
                      {[
                        { key: "demo", label: "Demo Available", icon: PlayCircle, on: d.demo },
                        { key: "purchase", label: "Purchase", icon: ShoppingBag, on: d.purchase },
                        { key: "installation", label: "Installation", icon: Wrench, on: d.installation },
                      ].map((s) => (
                        <li key={s.key} className="flex items-center gap-3" data-testid={`dealer-${i}-${s.key}`}>
                          <span className={`h-5 w-5 inline-flex items-center justify-center shrink-0 ${s.on ? "bg-gold text-charcoal-900" : "bg-charcoal-100 dark:bg-white/10 text-charcoal-400 dark:text-white/30"}`}>
                            {s.on ? <Check className="h-3 w-3" strokeWidth={3} /> : <X className="h-3 w-3" strokeWidth={2.5} />}
                          </span>
                          <span className={`text-sm ${s.on ? "text-foreground" : "text-muted-foreground line-through opacity-60"}`}>
                            {s.label}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </Reveal>
            ))}
            {!loading && dealers.length === 0 && (
              <div className="bg-background p-12 text-center text-sm text-muted-foreground">
                No dealers found for the selected filters.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Enquiry */}
      <section className="py-24 md:py-32 bg-charcoal-900 text-white" data-testid="dealer-enquiry">
        <div className="container-edge grid lg:grid-cols-12 gap-12 lg:gap-20">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow text-white/70 flex items-center gap-3"><span className="h-px w-12 bg-gold" /> Partner Enquiry</p>
            <h2 className="display-2 mt-5 text-white">
              Request <span className="italic text-gold">partnership</span>.
            </h2>
            <p className="mt-8 text-white/70 leading-relaxed">
              Tell us about your business. Our partnerships team will get back within two working days
              with next steps, training and territory options.
            </p>
          </Reveal>
          <Reveal delay={120} className="lg:col-span-7">
            <div className="text-white [&_input]:text-white [&_textarea]:text-white [&_label]:!text-white/60 [&_input]:!border-white/30 [&_textarea]:!border-white/30 [&_input:focus]:!border-gold [&_textarea:focus]:!border-gold">
              <InquiryForm
                type="dealer"
                testid="dealer-form"
                sourcePage="dealers"
                fields={["name", "company", "city", "state", "phone", "email", "current_brands", "experience_years", "message"]}
                submitLabel="Request Partnership"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
